import React from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import type { OverrideField, OverrideFieldSettings } from '../services/integrationApi';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { INTEGRATION_SETTINGS_COPY as COPY } from '../utils/integrationSettingsCopy';

export const OVERRIDE_FIELD_ORDER: OverrideField[] = [
  'title',
  'description',
  'images',
  'tags',
  'vendorStatus',
  'productType',
  'weight',
];

interface OverrideFieldsPickerProps {
  value?: OverrideFieldSettings;
  onChange: (next: OverrideFieldSettings) => void;
  /** Images can only be replaced when the Images data sync toggle is on. */
  imagesSyncEnabled: boolean;
  /** The rows to show, in order (a platform that only honours some fields). Defaults to every field. */
  fields?: OverrideField[];
  /** Per-platform wording for a row, title, subtitle or footnote. */
  copy?: {
    title?: string;
    subtitle?: string;
    keepNote?: string;
    fields?: Partial<Record<OverrideField, { label: string; desc: string }>>;
  };
}

/**
 * Per-field takeover choices shown under the Override sync mode. A missing key
 * means "replace" so integrations saved before this existed behave as before.
 */
const OverrideFieldsPicker: React.FC<OverrideFieldsPickerProps> = ({
  value,
  onChange,
  imagesSyncEnabled,
  fields: shownFields = OVERRIDE_FIELD_ORDER,
  copy,
}) => {
  const fields = { ...COPY.overrideFields.fields, ...(copy?.fields || {}) };
  const { t } = useAppTranslation();
  const { enabled: shippingEstimateOn } = useOrgFeatureFlag('shipping_weight_fallback');
  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {copy?.title ?? COPY.overrideFields.title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45, mb: 0.75 }}>
        {copy?.subtitle ?? COPY.overrideFields.subtitle}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {shownFields.map((field) => {
          const checked = value?.[field] !== false;
          const disabled = field === 'images' && !imagesSyncEnabled;
          return (
            <Box
              key={field}
              component="label"
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1 }}
            >
              <Checkbox
                size="small"
                checked={checked && !disabled}
                disabled={disabled}
                onChange={(e) => onChange({ ...(value || {}), [field]: e.target.checked })}
                sx={{ p: 0.5, mt: -0.25 }}
                inputProps={{ 'aria-label': fields[field].label }}
              />
              <Box sx={{ py: 0.25 }}>
                <Typography variant="body2">{fields[field].label}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                  {fields[field].desc}
                </Typography>
                {field === 'weight' && shippingEstimateOn && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                    {t('products.shippingEstimate.overrideNote')}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        {copy?.keepNote ?? COPY.overrideFields.keepNote}
      </Typography>
    </Box>
  );
};

export default OverrideFieldsPicker;
